const dayWithoutTime = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayHere(year, month, day) {
  const date = new Date(year, month - 1, day);
  const exists = date.getMonth() === month - 1 && date.getDate() === day;
  return exists ? date : new Date(NaN);
}

// Reads a value the way the forms have relied on: nothing is now, null is no date, and a day without a time
// is that day here, where Date alone reads it as midnight in UTC.
export function toDate(value) {
  if (value === undefined) return new Date();
  if (value === null) return new Date(NaN);
  const day = typeof value === "string" && dayWithoutTime.exec(value);
  return day ? dayHere(Number(day[1]), Number(day[2]), Number(day[3])) : new Date(value);
}

export function isValid(date) {
  return !Number.isNaN(date.getTime());
}

const tokens = /YYYY|MMMM|MMM|MM|DD|D|ddd|HH|mm|ss/g;

// Writes a date after a pattern of the tokens above, with the names of months and days in the given locale.
export function formatDate(value, pattern, locale) {
  const date = toDate(value);
  if (!isValid(date)) return "Invalid date";
  const name = (options) => new Intl.DateTimeFormat(locale, options).format(date);
  const digits = (number, length) => String(number).padStart(length, "0");
  const written = {
    YYYY: () => digits(date.getFullYear(), 4),
    MMMM: () => name({ month: "long" }),
    MMM: () => name({ month: "short" }),
    MM: () => digits(date.getMonth() + 1, 2),
    DD: () => digits(date.getDate(), 2),
    D: () => String(date.getDate()),
    ddd: () => name({ weekday: "short" }),
    HH: () => digits(date.getHours(), 2),
    mm: () => digits(date.getMinutes(), 2),
    ss: () => digits(date.getSeconds(), 2),
  };
  return pattern.replace(tokens, (token) => written[token]());
}

// Moves a date a number of months, to the last day of the month it lands in when that month is shorter.
export function addMonths(value, months) {
  const date = toDate(value);
  const month = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  date.setFullYear(month.getFullYear(), month.getMonth(), Math.min(date.getDate(), lastDay));
  return date;
}

// The whole years from a date to a later one, as an age is counted.
export function yearsBetween(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  const years = end.getFullYear() - start.getFullYear();
  return end < addMonths(start, years * 12) ? years - 1 : years;
}

// The whole days from one date to another by the clock on the wall, so that a change to or from summer time
// does not leave a day an hour short.
export function daysBetween(from, to) {
  const onTheWall = (value) => {
    const date = toDate(value);
    return date.getTime() - date.getTimezoneOffset() * 60000;
  };
  return Math.trunc((onTheWall(to) - onTheWall(from)) / 86400000);
}
